// Libraries
import React, { useState, useEffect, memo } from 'react'
import { Button, Grid, Typography } from '@material-ui/core'

// Components
import Dragzone from 'components/commons/Dragzone'
import ListSelector from 'components/commons/ListSelector'
import CustomModal from 'components/commons/CustomModal'

// Subcomponents
import ErrorsTable from './ErrorTable'

// Actions / Utils / Context
import { getFromApi, postToApi } from 'actions'
import { useStateValue } from 'context/store'
import { useApiRequest } from 'utils/customHooks/useApiRequest'

// Icons
import ErrorIcon from '@material-ui/icons/Error'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined'

const ERRORS_TABLE_INITAL_STATE = {
   fileName: '',
   data: []
}

const BulkImportUsers = () => {
   const style = {
      container: {
         margin: '10px 0px 10px 10px',
         paddingRight: '20px',
         justifyContent: 'flex-end'
      },
      titleContainer: {
         display: 'flex',
         alignItems: 'center'
      },
      fileLoadContainer: {
         width: '100%',
         padding: '0 16px',
         margin: '16px 0',
         display: 'flex',
         justifyContent: 'space-between'
      },
      buttomModal: {
         margin: '0 10px 0 10px'
      },
      downloadTemplate: {
         alignSelf: 'center'
      },
      downladLink: {
         textDecoration: 'none',
         color: '#0288D1'
      },
      confirmModalContainer: {
         display: 'flex',
         flexDirection: 'column',
         justifyContent: 'center',
         padding: '1.6rem 3rem',
         gap: '1rem'
      },
      modalButtonsContainer: {
         width: '100%',
         display: 'flex',
         justifyContent: 'space-evenly',
         marginTop: '.7rem'
      },
      iconContainer: {
         alignSelf: 'center',
         marginBottom: 16
      },
      errorIcon: {
         color: '#F01E46',
         fontSize: 68
      },
      successIcon: {
         color: '#37C846',
         fontSize: 68
      },
      warningIcon: {
         color: '#EED202',
         fontSize: 68
      },
      confirmationText: {
         marginBottom: 16,
         fontWeight: 'bold'
      },
      tableContainer: {
         width: '100%'
      }
   }

   const [, dispatch] = useStateValue()

   const [dragzoneAnchorEl, setDragzoneAnchorEl] = useState(null)

   const [rolesList, setRolesList] = useState([])
   const [applicationsList, setApplicationsList] = useState([])
   const [selectedRoles, setSelectedRoles] = useState([])
   const [applicationSelected, setApplicationSelected] = useState([])
   const [keyRefresh, setKeyRefresh] = useState(0) // TODO: Sintoma de que está mal manejado el estado
   const [confirmationMessage, setConfirmationMessage] = useState({
      message: '',
      error: false
   })
   const [fileForReqConfirmation, setFileForReqConfirmation] = useState(null)
   const [tableData, setTableData] = useState(ERRORS_TABLE_INITAL_STATE)

   useEffect(() => {
      loadApplicationsList()
   }, [])

   useEffect(() => {
      loadRoleList()
      setKeyRefresh(oldKey => oldKey + 1)
   }, [applicationSelected])

   const loadApplicationsList = () => {
      dispatch({ type: 'OPEN_SPINNER' })
      // TODO: refactor a async await con useApiRequest
      getFromApi(`${process.env.REACT_APP_AUTHORIZATION_API}/user/applications`)
         .then(response => {
            const newAppList = response.map(appData => ({
               id: appData.code,
               ...appData
            }))
            setApplicationsList(newAppList)
            dispatch({ type: 'CLOSE_SPINNER' })
         })
         .catch(error => {
            dispatch({ type: 'CLOSE_SPINNER' })
            dispatch({
               type: 'OPEN_SNACKBAR',
               openMessage: {
                  severity: 'error',
                  message: error?.message || 'Lo sentimos, ha ocurrido un error'
               }
            })
         })
   }

   const loadRoleList = () => {
      dispatch({ type: 'OPEN_SPINNER' })
      const urlParams = applicationSelected.join()
      // TODO: refactor a async await con useApiRequest
      getFromApi(
         `${process.env.REACT_APP_AUTHORIZATION_API}/user/applications/roles?applicationCodes=${urlParams}`
      )
         .then(response => {
            setRolesList(response)
            dispatch({ type: 'CLOSE_SPINNER' })
         })
         .catch(error => {
            dispatch({ type: 'CLOSE_SPINNER' })
            dispatch({
               type: 'OPEN_SNACKBAR',
               openMessage: {
                  severity: 'error',
                  message: error?.message || 'Lo sentimos, ha ocurrido un error'
               }
            })
         })
   }

   const roleSelectedCallback = roles => {
      setSelectedRoles(roles)
   }

   const applicationSelectedCallback = application => {
      setApplicationSelected(application)
   }

   async function _handleSaveFile(file) {
      const payload = new FormData()
      payload.append('file', file)
      setFileForReqConfirmation(null)
      let urlParams
      selectedRoles.forEach(rol => {
         urlParams = (urlParams || '') + `${urlParams ? '&' : ''}roles=${rol}`
      })
      const url = `${process.env.REACT_APP_USERS_API}/users/import?${urlParams}`
      const { errors, message } = await postToApi(url, payload)
      const errorEntries = Object.entries(errors)
      if (errorEntries.length) {
         setConfirmationMessage({
            message: 'Se presentaron errores procesando los usuarios',
            error: true
         })
         const dataErrors = processErrorData(errorEntries)
         setTableData({
            fileName: file.name,
            data: dataErrors
         })
      } else {
         setConfirmationMessage({
            message,
            error: false
         })
      }
   }

   const onSaveFile = useApiRequest(
      () => _handleSaveFile(fileForReqConfirmation),
      'Importar usuarios: registrar'
   )

   function onDragzoneFileAccepted(arrayOfFiles) {
      setFileForReqConfirmation(arrayOfFiles[0])
   }

   function onDragzoneFileRejection() {
      dispatch({
         type: 'OPEN_SNACKBAR',
         openMessage: {
            message: 'Solo se permiten archivos con extensión .csv',
            severity: 'error'
         }
      })
   }

   function openDragzone(event) {
      setDragzoneAnchorEl(event.currentTarget)
   }

   const ConfirmationModal = memo(_ => (
      <CustomModal showModal={!!confirmationMessage.message}>
         <Grid style={style.confirmModalContainer}>
            <div style={style.iconContainer}>
               {confirmationMessage.error ? (
                  <ErrorIcon style={style.errorIcon} />
               ) : (
                  <CheckCircleIcon style={style.successIcon} />
               )}
            </div>
            <Typography style={style.confirmationText}>
               {confirmationMessage.message}
            </Typography>
            <div style={style.modalButtonsContainer}>
               <Button
                  style={style.buttomModal}
                  variant="contained"
                  disableElevation
                  onClick={() =>
                     setConfirmationMessage({
                        message: '',
                        error: false
                     })
                  }
                  color="primary"
               >
                  Aceptar
               </Button>
            </div>
         </Grid>
      </CustomModal>
   ))

   function onReqConfirmAccept() {
      onSaveFile()
      setTableData(ERRORS_TABLE_INITAL_STATE)
   }

   const RequestConfirmationModal = memo(_ => (
      <CustomModal showModal={!!fileForReqConfirmation}>
         <Grid style={style.confirmModalContainer}>
            <div style={style.iconContainer}>
               <WarningOutlinedIcon style={style.warningIcon} />
            </div>
            <Typography style={style.confirmationText}>
               {`¿Desea cargar el archivo ${fileForReqConfirmation?.name}?`}
            </Typography>
            <div style={style.modalButtonsContainer}>
               <Button
                  style={style.buttomModal}
                  variant="contained"
                  disableElevation
                  color="primary"
                  onClick={onReqConfirmAccept}
               >
                  Aceptar
               </Button>
               <Button
                  style={style.buttomModal}
                  variant="outlined"
                  disableElevation
                  color="primary"
                  onClick={() => {
                     setFileForReqConfirmation(null)
                  }}
               >
                  Cancelar
               </Button>
            </div>
         </Grid>
      </CustomModal>
   ))

   return (
      <>
         <RequestConfirmationModal />
         <ConfirmationModal />
         <Grid container style={style.container}>
            <Grid item xs={12} className="mb10">
               <Grid style={style.titleContainer}>
                  <Typography variant="h6">
                     Importación masiva de usuarios
                  </Typography>
               </Grid>
            </Grid>
            <Grid item xs={12} sm={6} lg={6} xl={6}>
               <ListSelector
                  listItem={applicationsList}
                  title="Seleccione la Aplicación"
                  onlySelection={true}
                  listSelectionCallback={applicationSelectedCallback}
               />
            </Grid>
            <Grid item xs={12} sm={6} lg={6} xl={6}>
               <ListSelector
                  key={keyRefresh}
                  listItem={rolesList}
                  title="%n% Roles seleccionados"
                  listSelectionCallback={roleSelectedCallback}
               />
            </Grid>
            <Grid style={style.fileLoadContainer}>
               <div style={style.downloadTemplate}>
                  Descargue la plantilla de ejemplo{' '}
                  <a
                     href={`${process.env.REACT_APP_IMPORT_USERS_TEMPLATE}/file-resources/import-users.csv`}
                     download
                     style={style.downladLink}
                  >
                     aquí
                  </a>
               </div>
               <div>
                  <Button
                     color="primary"
                     disableElevation
                     disabled={!selectedRoles.length}
                     variant="contained"
                     onClick={openDragzone}
                  >
                     Cargar Archivo
                  </Button>
                  <Dragzone
                     anchorEl={dragzoneAnchorEl}
                     setAnchorEl={setDragzoneAnchorEl}
                     acceptCallback={onDragzoneFileAccepted}
                     rejectCallback={onDragzoneFileRejection}
                  />
               </div>
            </Grid>
            {!!selectedRoles.length && !!tableData.data.length && (
               <Grid style={style.tableContainer}>
                  <ErrorsTable tableData={tableData} />
               </Grid>
            )}
         </Grid>
      </>
   )
}

export default BulkImportUsers

// --------------------------------------------------

function processErrorData(errorEntries) {
   const errorList = []
   errorEntries.forEach(entry => {
      const [lineNumber, arrayOfErrorTexts] = entry
      errorList.push({ lineNumber, arrayOfErrorTexts })
   })
   return errorList
}
